import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from '../QuickQuote/Select';
import TextField from '../QuickQuote/TextField';
import TripType from './TripType';
import LocationType from './LocationType';
import AutoComplete from '../QuickQuote/AutoComplete';
import AutoCompleteDropdown from '../QuickQuote/AutoCompleteDropdown';
import RippleButton from 'components/PartnerPage/RippleButton';
import { useCruiseDataApi } from 'hooks/useCruiseDataApi';
import { useAutoCompleteApi } from 'hooks/useAutoCompleteApi';

function CruiseQuickQuoteForm({
  buttonText = 'Unlock effortless travel',
  breakText = 'Rates start at $65',
  fromTitle,
  toTitle,
  supportPhoneNumber
}) {
  const [tripType, setTripType] = useState({ id: 'to-cruise', label: 'To Cruise' });
  const [locationType, setLocationType] = useState('direct');
  const [cruiseLines, setCruiseLines] = useState(null);
  const [cruiseOptions, setCruiseOptions] = useState(null);
  const [cruisePorts, setCruisePorts] = useState([]);
  const [cruiseShips, setCruiseShips] = useState([]);
  const [itineraries, setItineraries] = useState([]);

  const [partnerId] = useState(window.gon?.partner_id);
  const [currentCruiseLine, setCurrentCruiseLine] = useState(null);
  const [itineraryFilterField, setItineraryFilterField] = useState('ship_name');
  const [itineraryFilterLabel, setItineraryFilterLabel] = useState('Ship Name');
  const [currentPort, setCurrentPort] = useState(null);
  const [currentShip, setCurrentShip] = useState(null);
  const [currentItinerary, setCurrentItinerary] = useState(null);
  const [fromAddress, setFromAddress] = useState(null);
  const [toAddress, setToAddress] = useState(null);

  const { getAutoComplete } = useAutoCompleteApi();
  const { getCruiseData, loading: loadingCruises } = useCruiseDataApi();

  const currentTitle = tripType.id === 'from-cruise' ? fromTitle : toTitle;

  const handleSetTripType = useCallback((tripType) => {
    setTripType(tripType);
    // reset the product fields
    setCurrentPort(null);
    setCruiseShips(null);
    setCurrentShip(null);
    setItineraries(null);
    setCurrentItinerary(null);
  }, [
    setCurrentPort, setCruiseShips, setCurrentShip,
    setItineraries, setCurrentItinerary
  ]);

  const handlePortSelect = useCallback((portName) => {
    if (!currentCruiseLine?.cruise_ports?.length) return;

    const port = currentCruiseLine.cruise_ports.filter((port) => {
        return (port.name === portName);
    });
    setCurrentPort(port[0]);
    setCurrentShip(null);
    setCurrentItinerary(null);

    /* the from/to address gets sent to the port agent address */
    if(port.length > 0) {
        if(tripType.id === 'from-cruise')
            setFromAddress(port[0].address);
        else
            setToAddress(port[0].address);
    }

    if (!currentCruiseLine) return ([]);
    const ship_map = {}

    currentCruiseLine.cruise_sessions?.forEach((item) => {
      if (tripType.id === 'from-cruise' && port[0]['port_id'] === item['disembarkation_port_id']) { // Fixed: added .id
        ship_map[ item[itineraryFilterField] ] = 1;
      } else if (port[0]['port_id'] === item['embarkation_port_id']) {
        ship_map[ item[itineraryFilterField] ] = 1;
      }
    });
    const ships = Object.keys(ship_map).sort();

    setCruiseShips(ships);
  }, [currentCruiseLine, tripType, itineraryFilterField]);

  useEffect(() => {
    const updateAddressWithPlaceId = async () => {
      if (currentPort) {
          const address = currentPort.address;
          const fullAddress = `${address.port_agent_name} ${address.delivery_address_line}, ` +
                              `${address.city}, ${address.state} ${address.country_code}`;
          if (address.google_place_id && address.google_place_id.length > 0) {
            if(tripType.id !== 'from-cruise') {
              setToAddress(address);
            } else {
              setFromAddress(address);
            }
            return; // No need to fetch place ID if it already exists
          }

          const result = await getAutoComplete(fullAddress);

          if (result.success) {
              const ids = result.data.filter((item) => {
                  return (item._id && item._id !== 'DZIP-' && item._type === 'GooglePlace');
              });

              if (ids.length > 0) {
                  console.log('setting', ids);
                  if(tripType.id !== 'from-cruise') {
                      setToAddress(prevToAddress => {
                          if (prevToAddress && prevToAddress.google_place_id !== ids[0]['_id']) {
                              return {
                                  ...prevToAddress,
                                  google_place_id: ids[0]['_id']
                              };
                          }
                          return prevToAddress;
                      });
                  } else {
                      setFromAddress(prevFromAddress => {
                         if (prevFromAddress && prevFromAddress.google_place_id !== ids[0]['_id']) {
                              return {
                                  ...prevFromAddress,
                                  google_place_id: ids[0]['_id']
                              };
                          }
                          return prevFromAddress;
                      });
                  }
              }
          }
      }
    };

    updateAddressWithPlaceId();
  }, [currentPort, tripType.id]);

  const handleShipSelect = useCallback((ship) => {
    if (!currentCruiseLine || !ship) return;

    setCurrentShip(ship);
    setCurrentItinerary(null);
    setItineraries([]);

    // get the itinerary for the port and ship
    const itineraries = [];
    currentCruiseLine.cruise_sessions?.forEach((item) => {
      if(item[itineraryFilterField] !== ship) return;
        if(tripType.id === 'round-trip' &&
          item['embarkation_port_id'] === item['disembarkation_port_id'])
           itineraries.push(item);
        else if(tripType.id === 'from-cruise' &&
          currentPort['port_id'] === item['disembarkation_port_id']) {
            itineraries.push(item);
        } else if(tripType.id === 'to-cruise' &&
          currentPort['port_id'] === item['embarkation_port_id']) {
            itineraries.push(item);
        }
    });
    setItineraries(itineraries);
  }, [currentCruiseLine, currentPort, tripType, itineraryFilterField]); // Added missing dependency

  const formatItinerary = useCallback((item) => {
    if(!item) return null;

    return (
        <div data-itinerary-id={item.id} className="flex flex-row justify-between">
            <span className="me-2">
              {`${item.embarkation_date}`}
              {' - '}
              {`${item.disembarkation_date}`}
              {' | '}
              {`${item.embarkation_port}`}
              {' to '}
              {`${item.disembarkation_port}`}
            </span>
        </div>
    )
  }, []); // Added empty dependency array

  const isDisabled = !currentCruiseLine || !currentPort || !currentShip || !currentItinerary || loadingCruises;

  const createPortOptions = useCallback((ports) => {
    const item_map = {};
    ports.forEach((port) => {
        item_map[ port.name ] = 1;
    });
    const items = Object.keys(item_map);
    return (items.sort());
  }, []);

  const determineToField = (currentCruiseLine, cruiseOptions) => {
    if (currentCruiseLine) {
        return (
            <TextField label={'To'}
                text={currentCruiseLine.name}
                textClass={'text-stone-800'}
                bgClass={'bg-[#e5e5e5]'}
            />
        )
    }

    if (cruiseOptions) {
        return (<AutoCompleteDropdown
                    label="To"
                    options={cruiseOptions}
                    onSelect={(address) => setToAddress(address)}
                />);
    }
    return (
        <AutoComplete
          label="To"
          placeholder="Destination"
          onSelect={(address) => setToAddress(address)}
          />
      );
  }

  const determineFromField = (currentCruiseLine, cruiseOptions) => {
    if (currentCruiseLine) {
        return (
            <TextField label={'From'}
                text={currentCruiseLine.name}
                textClass={'text-stone-800'}
                bgClass={'bg-[#e5e5e5]'}
            />
        )
    }

    if (cruiseOptions) {
        return (<AutoCompleteDropdown
                    label="From"
                    options={cruiseOptions}
                    onSelect={(address) => setFromAddress(address)}
                />
              );
    }
    return (
        <AutoComplete
          label="From"
          placeholder="Origin"
          onSelect={(address) => setFromAddress(address)}
          />
      );
  }

  // Fixed: Proper async handling in useEffect
  useEffect(() => {
    const fetchCruiseData = async () => {
      if (!cruiseLines) {
        const results = await getCruiseData({
          partnerId: window.gon?.partner_id
        });
        if(results.data) {
          const data = results.data;
          setCruiseLines(data);
          const options = data.map((cruise) => ({
              'id': cruise.club_id,
              'icon_url': cruise.icon_url,
              'formatted_address': cruise.name
          }));
          // if we have a partner id we will use that
          // to lock in the cruise partner name
          if(options.length === 1 && partnerId) {
              const cruiseLine = results.data[0];
              setCurrentCruiseLine(cruiseLine);
              const firstSession = cruiseLine?.cruise_sessions?.[0];
              if (firstSession && firstSession.tour_name && firstSession.tour_name !== '') {
                  setItineraryFilterField('tour_name');
                  setItineraryFilterLabel('Tour Name');
              }
              localStorage.setItem('currentCruiseLine', JSON.stringify(results.data[0]));
              localStorage.setItem('cruisePremiumPartner-' + partnerId, window.location.href);
              const portOptions = createPortOptions(results.data[0]?.cruise_ports || []);
              setCruisePorts(portOptions);
          }
          setCruiseOptions(options);
        }
      }
    };

    fetchCruiseData();
  }, [cruiseLines, getCruiseData, partnerId, createPortOptions]);

  const toField = determineToField(currentCruiseLine, cruiseOptions);
  const fromField = determineFromField(currentCruiseLine, cruiseOptions);

  const handleSubmit = useCallback(() => {
    let o_Address = {};
    let d_Address = {};
    let cruiseOrTour = {}

    const select_field = itineraryFilterField === 'tour_name' ? 'cruise_tour_name' : 'cruise_ship';
    cruiseOrTour[select_field] = currentShip;

    if (tripType.id !== 'from-cruise') {
        o_Address = {
            o_name: fromAddress.formatted_address,
            o_type: 'GooglePlace',
            o_id: fromAddress._id
        }
        // port is the to address
        d_Address = {
            d_name: `${toAddress.delivery_address_line}, ${toAddress.city}, ` +
                    `${toAddress.state} ${toAddress.country_code}`,
            d_type: 'GooglePlace',
            d_id: toAddress.google_place_id,
            d_cruiseline_id: currentCruiseLine.id,
            d_port_id: currentPort.id
        }
    }
    if (tripType.id === 'from-cruise') {
         // port is the from address
         o_Address = {
            o_name: `${fromAddress.delivery_address_line}, ${fromAddress.city}, ` +
                    `${fromAddress.state} ${fromAddress.country_code}`,
            o_type: 'GooglePlace',
            o_id: fromAddress.google_place_id,
            o_cruiseline_id: currentCruiseLine.id,
            o_port_id: currentPort.id
        }

        d_Address = {
            d_name: toAddress.formatted_address,
            d_type: 'GooglePlace',
            d_id: toAddress._id
        }
    }

    const params = new URLSearchParams({
        cruise: 'true',
        trip_type: tripType.id,
        ...o_Address,
        ...d_Address,
        cruise_line: currentCruiseLine.name,
        cruise_port: currentPort?.name,
        cruise_port_id: currentPort?.port_id,
        ...cruiseOrTour,
        partner_id: window.gon?.partner_id,
        cruise_session_id: currentItinerary.id,
        embarkation_date: currentItinerary.embarkation_date,
        disembarkation_date: currentItinerary.disembarkation_date
    });

    if(window.gon?.partner_id) {
      // set partner id for pricing page filtering
      window.sessionStorage.setItem('partner_id', window.gon.partner_id);
      // set cruise_partner id for cleanup
      window.sessionStorage.setItem('cruise_partner_id', window.gon.partner_id);
    }
    const url = `/pricing/?${params.toString()}`;
    window.location.href = url;
  }, [
    tripType,
    fromAddress,
    toAddress,
    currentCruiseLine,
    currentPort,
    currentShip,
    currentItinerary,
    itineraryFilterField
  ]);

  return (
    <form style={{minHeight: '700px'}} className="w-full h-full" onSubmit={(event) => {
      event.preventDefault();
      if (!loadingCruises && fromAddress &&
           toAddress && currentCruiseLine && currentPort &&
           currentShip && currentItinerary) {
        handleSubmit();
      }
    }}>
      <div className="text-[20px] font-semibold text-[#1C2938] mb-5 md:pt-3 lg:px-4">
          {currentTitle}
      </div>

      <div className="w-full h-full flex flex-col justify-between">
        <TripType
            selectedType={tripType}
            onTypeChange={handleSetTripType}
            onSetLocationType={setLocationType}
        />
        {(tripType.id === 'to-cruise' ||
          tripType.id === 'from-cruise') && (
            <LocationType
            tripType={tripType}
            selectedType={locationType}
            onTypeChange={setLocationType}
        />
        )}
        <div className="flex flex-col gap-2 justify-around">
                {((locationType === 'direct' && tripType.id === 'to-cruise')) && (
                    <>
                        <AutoComplete
                        label="From"
                        placeholder="Pickup or drop off location"
                        onSelect={(address) => setFromAddress(address)}
                        />

                        {toField}

                        <Select
                            label="Port"
                            placeholder="Select your Cruise Port"
                            options={cruisePorts}
                            onSelect={handlePortSelect}
                            canOpen={() => {
                                // address must exist first
                                return (tripType.id === 'from-cruise' ?
                                  !!toAddress : !!fromAddress);
                            }}
                            errorText={'You must enter an address before selecting port'}
                        />

                        <Select
                            label={itineraryFilterLabel}
                            placeholder={`Select your ${itineraryFilterLabel}`}
                            options={cruiseShips}
                            onSelect={handleShipSelect}
                            value={currentShip}
                            canOpen={() => {
                                // port must exist first
                                return (!!currentPort);
                            }}
                            errorText={`You must select Port before selecting ${itineraryFilterLabel}`}
                        />
                    </>
                )}
                {currentCruiseLine && console.log('current cruise line: ' + currentCruiseLine.name)}
                        {locationType === 'direct' &&
                         tripType.id === 'from-cruise' &&
                         currentCruiseLine && (
                    <>
                        {fromField}

                        <AutoComplete
                            label="To"
                            placeholder="Home Address"
                            onSelect={(address) => setToAddress(address)}
                        />

                        <Select
                            label="Port"
                            placeholder="Select your Cruise Port"
                            options={cruisePorts}
                            onSelect={handlePortSelect}
                            canOpen={() => {
                                // address must exist first
                                return (tripType.id === 'from-cruise' ?
                                  !!toAddress : !!fromAddress);
                            }}
                            errorText={'You must select enter an address before selecting Port'}
                        />

                        <Select
                            label={itineraryFilterLabel}
                            placeholder={`Select your ${itineraryFilterLabel}`}
                            options={cruiseShips}
                            value={currentShip}
                            onSelect={handleShipSelect}
                            canOpen={() => {
                                // address must exist first
                                return (!!currentPort);
                            }}
                            errorText={`You must select Port before selecting ${itineraryFilterLabel}`}
                        />
                    </>
                )}

                {tripType.id !== 'multi-city' &&
                 tripType.id !== 'round-trip' &&
                 locationType === 'direct' && (
                    <Select
                        label="Select Itinerary"
                        placeholder="Select your itinerary"
                        options={itineraries}
                        formatItem={formatItinerary}
                        onSelect={(itinerary) => setCurrentItinerary(itinerary)}
                        canOpen={() => {
                            // address must exist first
                            return (!!currentShip);
                        }}
                        errorText={'You must select Ship Name before selecting Itinerary'}
                    />
                )}

                {(tripType.id === 'multi-city' ||
                  tripType.id === 'round-trip' ||
                  locationType === 'another-location') && (
                        <>
                            <div className="flex flex-col items-center justify-between mt-5 bg-[#F8F9FB] m-2 p-6">
                                {locationType === 'another-location' && (
                                    <p>To ship your bags {tripType.id === 'from-cruise' ?
                                          ' from ' : ' to '} another location
                                        {tripType.id === 'from-cruise' ?
                                          ' after ' : ' before '} your cruise, {/* Use strict equality */}
                                        please contact customer support to complete your booking.
                                    </p>
                                )}
                                {locationType !== 'another-location' && (
                                <p>Please book two one-way orders for your&nbsp;
                                     {tripType.id.replace(/-/g, ' ')} order or contact
                                     customer service to complete your booking.</p>
                                )}
                                <p className="p-2">
                                    <span className="mt-5 mr-2 text-lg text-gray-600 font-bold">
                                        Need Help?
                                    </span>
                                    <span className="mt-5 mr-2 text-lg font-bold"> {/* Fixed: added missing space */}
                                        {supportPhoneNumber}
                                    </span>
                                </p>
                            </div>
                        </>
                    )}
                </div>

                {tripType.id === 'multi-city' && <div className="flex-grow"></div>} {/* Use strict equality */}

                <div className="w-full">
                    <div className="w-full flex items-center justify-center my-4 py-2 px-4">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="mx-4 text-gray-600 font-bold">
                        {breakText}
                        </span>
                        <div className="flex-grow border-t border-gray-300"></div>
                    </div>

                    <div className="w-full 2xl:w-100 mx-auto px-4 py-3">
                        <RippleButton
                        className={`
                            flex items-center justify-center bg-[#4FAB55] text-white rounded-md
                            py-2 px-4 h-[67px] w-full
                            ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                            type="submit"
                            disabled={isDisabled}
                            aria-label={buttonText}
                            style={{ transition: 'background-color 0.3s' }}
                            >
                        <span>{buttonText}</span>
                        </RippleButton>
                    </div>
                </div>
            </div>

    </form>
  );
}

CruiseQuickQuoteForm.propTypes = {
  allowedLocations: PropTypes.array,
  buttonText: PropTypes.string,
  breakText: PropTypes.string,
  fromTitle: PropTypes.string,
  toTitle: PropTypes.string,
  supportPhoneNumber: PropTypes.string
};

export default CruiseQuickQuoteForm;
